import ArticleLayout, { H2, H3, Lead, Note, BackHome } from "@/components/ArticleLayout";
import { IMAGES } from "@/lib/config";

export default function EnduitsChauxPlatreArgile() {
    return (
        <ArticleLayout
            slug="enduits-chaux-platre-argile"
            title={<>Enduits chaux, plâtre & argile : <span className="italic text-brand">quel enduit pour quel mur ?</span></>}
            metaTitle="Enduits chaux, plâtre & argile : quel enduit pour quel mur ?"
            subtitle="Un mur ancien mal enduit, c'est un mur qui pourrit lentement. Le choix du bon liant est plus important que celui du bon artisan."
            breadcrumbLabel="Enduits chaux, plâtre & argile"
            metaDescription="Guide comparatif des enduits pour bâti ancien : chaux aérienne (NHL), plâtre traditionnel, argile crue. Caractéristiques, compatibilité, mise en œuvre et coût — par un artisan-diagnostiqueur en Eure-et-Loir."
            coverImage={IMAGES.texture}
            coverAlt="Texture d'un enduit à la chaux aérienne appliqué à la truelle sur un mur ancien"
            updated="2026-01-15"
        >
            <Lead>
                Il n'existe pas <em>un</em> bon enduit pour le bâti ancien, mais une famille entière — chaque
                liant répond à une situation précise. Voici comment choisir sans se tromper.
            </Lead>

            <H2>La règle d'or : la compatibilité</H2>
            <p>
                Un enduit doit toujours être <strong>moins dur, plus perméable et plus souple que le support</strong>{" "}
                qu'il recouvre. Le ciment, dur et étanche, viole cette règle sur presque tous les supports
                anciens (pierre, torchis, pisé, brique de pays) — il fissure et retient l'eau contre le mur.
            </p>

            <H2>Les trois grandes familles</H2>

            <H3>1. La chaux — le passe-partout du bâti ancien</H3>
            <p>
                <strong>Chaux aérienne (CL) :</strong> durcit lentement au contact du CO₂, très souple, très
                perméable. Idéale en finition intérieure, en badigeon, en peinture. Extrêmement respectueuse
                des supports fragiles.
            </p>
            <p>
                <strong>Chaux hydraulique naturelle (NHL 2 / 3,5 / 5) :</strong> durcit au contact de l'eau,
                plus résistante mécaniquement. Idéale pour les corps d'enduit, les enduits extérieurs, les
                murs sollicités. La NHL 2 pour le très ancien, la NHL 3,5 en usage courant.
            </p>
            <p>
                <strong>Usage :</strong> partout ou presque. Sur bâti pierre, sur torchis, à l'extérieur
                comme à l'intérieur. Dosages typiques : 1 vol. chaux / 2,5-3 vol. sable.
            </p>

            <H3>2. Le plâtre traditionnel — la spécialité intérieure</H3>
            <p>
                Le plâtre gros de Paris, hydraté puis pris, était le standard des enduits intérieurs
                jusqu'au XX<sup>e</sup> siècle. Il a un avantage rare : <strong>il régule fortement
                l'hygrométrie de la pièce</strong> — il absorbe l'humidité de l'air quand elle est élevée
                et la restitue quand l'air s'assèche.
            </p>
            <p>
                <strong>Usage :</strong> intérieur uniquement (le plâtre n'aime pas l'eau liquide),
                cloisons, plafonds, corniches. Se travaille rapidement et se lisse à la spatule.
                Sensible : pas de plâtre sur mur humide.
            </p>

            <H3>3. L'argile & terre crue — la solution biosourcée par excellence</H3>
            <p>
                Enduit ancestral, l'enduit terre est fait d'argile, de sable et de fibres (paille, chanvre,
                lin). Non cuit, il est <strong>totalement recyclable</strong>, extrêmement perméable à la
                vapeur d'eau et régule l'hygrométrie mieux qu'aucun autre matériau. C'est aussi l'enduit
                le plus économique quand la terre est disponible sur place.
            </p>
            <p>
                <strong>Usage :</strong> corps d'enduit intérieur sur torchis ou pisé, finitions
                décoratives, murs isolants chaux-chanvre. Il craint l'eau liquide et se protège soit par
                une casquette (auvent, larmier) soit par une finition chaux ou badigeon.
            </p>

            <H2>Tableau récapitulatif rapide</H2>
            <div className="overflow-x-auto my-4">
                <table className="w-full text-sm border-collapse border border-border">
                    <thead>
                        <tr className="bg-sand/50 text-ink">
                            <th className="border border-border p-3 text-left">Enduit</th>
                            <th className="border border-border p-3 text-left">Usage</th>
                            <th className="border border-border p-3 text-left">Support</th>
                            <th className="border border-border p-3 text-left">Perspirance</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="border border-border p-3">Chaux aérienne</td>
                            <td className="border border-border p-3">Finition, badigeon, peinture</td>
                            <td className="border border-border p-3">Tous</td>
                            <td className="border border-border p-3">Excellente</td>
                        </tr>
                        <tr>
                            <td className="border border-border p-3">Chaux hydraulique NHL</td>
                            <td className="border border-border p-3">Corps d'enduit, extérieur</td>
                            <td className="border border-border p-3">Pierre, briques</td>
                            <td className="border border-border p-3">Très bonne</td>
                        </tr>
                        <tr>
                            <td className="border border-border p-3">Plâtre traditionnel</td>
                            <td className="border border-border p-3">Intérieur sec</td>
                            <td className="border border-border p-3">Pierre, torchis</td>
                            <td className="border border-border p-3">Bonne</td>
                        </tr>
                        <tr>
                            <td className="border border-border p-3">Terre / argile</td>
                            <td className="border border-border p-3">Intérieur, corps ou finition</td>
                            <td className="border border-border p-3">Torchis, pisé, pierre</td>
                            <td className="border border-border p-3">Exceptionnelle</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <H2>Les erreurs classiques à éviter</H2>
            <ul className="list-disc pl-6 space-y-2">
                <li><strong>Ciment sur pierre ancienne</strong> — provoque humidité et éclatement du support</li>
                <li><strong>Peinture acrylique sur enduit chaux</strong> — bloque la vapeur d'eau, l'enduit cloque</li>
                <li><strong>Enduit trop épais en une passe</strong> — retrait, fissures, décollement</li>
                <li><strong>Support pas humidifié</strong> — dessèchement rapide, faïençage</li>
                <li><strong>Sable trop fin</strong> — fissuration, résistance faible</li>
            </ul>

            <H2>Combien ça coûte ?</H2>
            <p>
                Ordres de grandeur en fourniture + pose (2026, Eure-et-Loir) :
            </p>
            <ul className="list-disc pl-6 space-y-2">
                <li>Enduit chaux extérieur — <strong>70 à 110 €/m²</strong> selon complexité</li>
                <li>Enduit intérieur chaux + finition — <strong>55 à 90 €/m²</strong></li>
                <li>Enduit terre corps + finition — <strong>60 à 100 €/m²</strong> selon accessibilité</li>
                <li>Badigeon de chaux 2 couches — <strong>18 à 30 €/m²</strong></li>
            </ul>

            <Note>
                Ces fourchettes intègrent la <strong>préparation du support</strong> (souvent négligée dans
                les devis low-cost) qui représente 30 à 50 % du chantier sur un mur ancien.
            </Note>

            <H2>Vous hésitez sur le choix d'un enduit ?</H2>
            <p>
                C'est précisément le rôle du diagnostiqueur : venir sur place, ausculter le mur, comprendre
                son histoire, mesurer son humidité et sa perméabilité, puis vous proposer le bon liant, la
                bonne granulométrie et la bonne stratégie de mise en œuvre. Sans marge sur les matériaux,
                sans conflit d'intérêt.
            </p>

            <Note tone="sand">
                📞 Un projet d'enduit ? Parlons-en :{" "}
                <a href="tel:+33664208134" className="text-brand hover:underline font-medium">06 64 20 81 34</a>.
            </Note>

            <BackHome />
        </ArticleLayout>
    );
}
